/**
 * Toolbar / FilterBar — the shared search + filters + actions row.
 *
 * One pattern for every table toolbar: it wraps on small viewports instead of
 * clipping, states how many filters are active, and offers a single reset. The
 * collapsible mobile filter drawer is a later phase; this component is shaped so
 * that it can host it without a second toolbar being invented.
 */
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export function SearchInput({
  value, onChange, placeholder = "Cerca…", className = "", testId = "toolbar-search", label,
}) {
  return (
    <div className={`relative flex-1 min-w-[200px] ${className}`}>
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-muted pointer-events-none"
        aria-hidden="true"
      />
      <input
        type="search"
        data-testid={testId}
        aria-label={label || placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-[var(--control-h-field)] pl-9 pr-11 rounded-md border border-input bg-surface
                   [&::-webkit-search-cancel-button]:appearance-none
                   text-sm text-content placeholder:text-content-muted shadow-sm
                   transition-[border-color,box-shadow] duration-base ease-system
                   focus-visible:outline-none focus-visible:border-focus focus-visible:ring-2 focus-visible:ring-focus/30"
      />
      {value && (
        <button
          type="button"
          data-testid={`${testId}-clear`}
          aria-label="Cancella la ricerca"
          onClick={() => onChange("")}
          className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10
                     rounded-md text-content-muted hover:text-content hover:bg-surface-subtle
                     transition-colors duration-fast ease-system"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export function Toolbar({ children, className = "", testId = "toolbar" }) {
  return (
    <div
      data-testid={testId}
      className={`flex flex-wrap items-center gap-2 ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * FilterBar — filters + active count + reset. `activeCount` is what makes the
 * state of the view legible ("stai guardando un sottoinsieme").
 */
export function FilterBar({
  children,
  activeCount = 0,
  onReset,
  resetLabel = "Reset filtri",
  status,
  className = "",
  testId = "filter-bar",
}) {
  return (
    <div
      data-testid={testId}
      className={`flex flex-wrap items-end gap-2 ${className}`}
    >
      {children}
      <div className="flex flex-wrap items-center gap-2 ml-auto">
        {status}
        {activeCount > 0 && (
          <span
            data-testid={`${testId}-active-count`}
            className="text-label text-content-muted"
          >
            {activeCount} {activeCount === 1 ? "filtro attivo" : "filtri attivi"}
          </span>
        )}
        {onReset && (
          <Button
            data-testid={`${testId}-reset`}
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={activeCount === 0}
            title={activeCount === 0 ? "Nessun filtro attivo" : resetLabel}
          >
            <X className="w-4 h-4" aria-hidden="true" />
            {resetLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

export default Toolbar;
